import { Box } from '@mui/joy';
import { AchievementBadge } from '../AchievementBadge/AchievementBadge';

/** Полный оборот вокруг аватара. Намеренно очень медленно — это фон, а не анимация. */
const ORBIT_DURATION = 100;
/** Полупериод «маятника» вокруг своей оси, секунды. */
const SWING_DURATION = 2.6;
/** Амплитуда маятника в градусах: покачивание, а не вращение. */
const SWING_ANGLE = 14;
/** Насколько значок отстоит от края аватара. */
const ORBIT_GAP = 4;

/**
 * Выбранные достижения, медленно кружащие вокруг аватара.
 *
 * Оборачивает аватар: сам аватар передаётся детьми, чтобы компонент не знал
 * того, как он отрисован. Слой со значками абсолютный, поэтому в поток он не
 * попадает и шапка профиля выглядит ровно так же, как без витрины.
 *
 * Как держится вертикаль: крутится всё кольцо целиком, а каждый значок внутри
 * него крутится с той же скоростью в обратную сторону — на экране он остаётся
 * «вертикальным», и уже поверх этого накладывается маятник.
 *
 * @param {Array<{id:number, code:string, title:string, image:string|null}>} achievements
 * @param {number} avatarSize - сторона аватара, от неё считается радиус
 * @param {React.ReactNode} children - сам аватар
 */
export function AchievementOrbit({ achievements = [], avatarSize, children }) {
    if (!Array.isArray(achievements) || achievements.length === 0) return children;

    const badgeSize = Math.max(24, Math.round(avatarSize * 0.28));
    const radius = avatarSize / 2 + ORBIT_GAP;

    return (
        <Box
            sx={{
                position: 'relative',
                width: avatarSize,
                height: avatarSize,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '@keyframes achievement-orbit': {
                    from: { transform: 'rotate(0deg)' },
                    to: { transform: 'rotate(360deg)' },
                },
                '@keyframes achievement-orbit-reverse': {
                    from: { transform: 'rotate(0deg)' },
                    to: { transform: 'rotate(-360deg)' },
                },
                '@keyframes achievement-swing': {
                    from: { transform: `rotate(-${SWING_ANGLE}deg)` },
                    to: { transform: `rotate(${SWING_ANGLE}deg)` },
                },
                // Значки — украшение: тем, кто просил меньше движения, отдаём
                // ту же витрину, только неподвижную.
                '@media (prefers-reduced-motion: reduce)': {
                    '& *': { animation: 'none !important' },
                },
            }}
        >
            {children}

            {/* pointerEvents: none — значки не должны перехватывать клик по аватару */}
            <Box
                aria-hidden
                sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    animation: `achievement-orbit ${ORBIT_DURATION}s linear infinite`,
                }}
            >
                {achievements.map((achievement, index) => {
                    const angle = (index * 360) / achievements.length;

                    return (
                        <Box
                            key={achievement.id}
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                width: badgeSize,
                                height: badgeSize,
                                mt: `${-badgeSize / 2}px`,
                                ml: `${-badgeSize / 2}px`,
                                // Место на окружности. Обратный поворот держит
                                // значок вертикальным относительно кольца.
                                transform: `rotate(${angle}deg) translateY(${-radius}px) rotate(${-angle}deg)`,
                            }}
                        >
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    animation: `achievement-orbit-reverse ${ORBIT_DURATION}s linear infinite`,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        // Фаза у каждого значка своя, иначе все качаются
                                        // синхронно и это читается как дрожание всей
                                        // группы, а не как отдельные подвески.
                                        animation: `achievement-swing ${SWING_DURATION}s ease-in-out ${index * 0.42}s infinite alternate`,
                                    }}
                                >
                                    <AchievementBadge
                                        code={achievement.code}
                                        image={achievement.image}
                                        size={badgeSize}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

export default AchievementOrbit;
